import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { ampli } from '@/ampli';

import { Section, SectionBody } from '@/components/Modal/GlobalMarketSettingsModal/Section';

import {
  CHAIN_IDS,
  CHAIN_IDS_SUPPORT_INTENTS,
  SUPPORTED_CHAINS_BY_CHAIN_ID,
} from '@/constants/chain';

import { useIntentsAvailability } from '@/hooks/useIntentsAvailability';

import { matchaAutoSelectors, useMarketTradeSettingsStore } from '@/store/tradeSettings/market';

import { linkStyles } from '@/styles/links';
import { fontStyles } from '@/styles/typography';

import { MatchaFlatGreenIcon } from '@/ui/Icons/MatchaFlatGreenIcon';
import { ExternalLink } from '@/ui/Link/ExternalLink';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { isRwaIssuer, TokenResult } from '@/utils/0x/token-registry.types';
import { joinWithDifferentLastSeparator } from '@/utils/customJoin';
import { isTokenAddressNativeAsset } from '@/utils/multichain';
import { routes } from '@/utils/routes';

type ToggleOption = 'on' | 'off';

interface Props {
  /** Buy token */
  buyToken: TokenResult | undefined;
  /** Chain ID */
  chainId: number;
  /** Whether the active pair spans two chains. */
  isCrossChain?: boolean;
  /** Sell token */
  sellToken: TokenResult | undefined;
}

export const MatchaAutoSwitchSection = ({ buyToken, chainId, isCrossChain, sellToken }: Props) => {
  const { setIsMatchaAutoOn } = useMarketTradeSettingsStore(matchaAutoSelectors);
  const {
    isAvailable: isIntentsAvailable,
    isEnabled: isIntentsEnabled,
    unavailableReason,
  } = useIntentsAvailability({ buyToken, sellToken, chainId });
  const isGaslessToggleEnabled = !isCrossChain && isIntentsAvailable;

  const rwaTokenSymbol = isRwaIssuer(sellToken?.issuer ?? null)
    ? sellToken?.symbol
    : isRwaIssuer(buyToken?.issuer ?? null)
      ? buyToken?.symbol
      : undefined;

  const selectedOption: ToggleOption = isIntentsEnabled && isGaslessToggleEnabled ? 'on' : 'off';

  const handleOnValueChange = (value: ToggleOption | '') => {
    if (!isGaslessToggleEnabled) return;
    // Radix sends '' (deselect) when the selected item is re-clicked; treat
    // it as confirming the displayed option so the choice becomes explicit.
    const next = value === '' ? selectedOption : value;
    setIsMatchaAutoOn(next === 'on');
    ampli.matchaAutoSettingUpdated({
      ui_state: next,
    });
  };

  const supportedChainNames = CHAIN_IDS_SUPPORT_INTENTS.map(
    (chainId) => SUPPORTED_CHAINS_BY_CHAIN_ID[chainId].name,
  );
  const supportedChainsPhrase = joinWithDifferentLastSeparator(supportedChainNames, ', ', ' or ');

  return (
    <Section>
      <SectionBody>
        <div className="flex justify-between items-center -mt-3">
          <div className={fontStyles({ variant: 'LABEL_MD', class: 'flex items-center' })}>
            <MatchaFlatGreenIcon className="mr-2" />
            Matcha Auto
          </div>

          <ToggleGroup.Root
            className="bg-token-gray-300 flex my-3 p-1 rounded-[1.75rem] justify-between"
            aria-label="Choose to enable Matcha Auto"
            type="single"
            value={selectedOption}
            onValueChange={handleOnValueChange}
          >
            {isGaslessToggleEnabled ? (
              <ToggleGroup.Item
                className={fontStyles({
                  variant: 'LABEL_MD',
                  class:
                    'text-token-gray-1600 flex w-full h-7 px-4 border-none items-center rounded-lg justify-center hover opacity-100 aria-checked:bg-special-background-button-medium-secondary aria-checked:shadow-05 aria-checked:transition-all aria-checked:duration-200 aria-checked:opacity-100 disabled:text-token-gray-900 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-token-gray-900 disabled:hover:cursor-not-allowed disabled:hover:opacity-50 disabled:aria-checked:text-token-gray-900 disabled-aria-checked:cursor-not-allowed disabled:aria-checked:opacity-50 focus-visible:outline-2 focus-visible:outline-token-gray-400 focus:active:outline-0 mr-1',
                })}
                value="on"
                disabled={!isGaslessToggleEnabled}
              >
                On
              </ToggleGroup.Item>
            ) : (
              <MatchaTooltip>
                <MatchaTooltipTrigger>
                  <ToggleGroup.Item
                    className={fontStyles({
                      variant: 'LABEL_MD',
                      class:
                        'text-token-gray-1600 flex w-full h-7 px-4 border-none items-center rounded-lg justify-center hover opacity-100 aria-checked:bg-special-background-button-medium-secondary aria-checked:shadow-05 aria-checked:transition-all aria-checked:duration-200 aria-checked:opacity-100 disabled:text-token-gray-900 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-token-gray-900 disabled:hover:cursor-not-allowed disabled:hover:opacity-50 disabled:aria-checked:text-token-gray-900 disabled-aria-checked:cursor-not-allowed disabled:aria-checked:opacity-50 focus-visible:outline-2 focus-visible:outline-token-gray-400 focus:active:outline-0 mr-1',
                    })}
                    value="on"
                    disabled={!isGaslessToggleEnabled}
                  >
                    On
                  </ToggleGroup.Item>
                </MatchaTooltipTrigger>
                <MatchaTooltipContent>
                  {isCrossChain ? (
                    <p>Matcha Auto is not available for cross-chain swaps.</p>
                  ) : unavailableReason === 'geo-checking' ? (
                    <p>
                      Checking whether Matcha Auto is available for these tokens in your region.
                    </p>
                  ) : unavailableReason === 'geo-blocked' ? (
                    <p>
                      Matcha Auto is not available for RWA tokens in your region. Use standard swap
                      mode to trade {rwaTokenSymbol ?? 'these tokens'}.
                    </p>
                  ) : unavailableReason === 'monad-smart-wallet' ? (
                    <p>
                      Matcha Auto is not available for smart wallets on Monad. Switch to a different
                      network to use Matcha Auto.
                    </p>
                  ) : CHAIN_IDS_SUPPORT_INTENTS.includes(sellToken?.chainId || CHAIN_IDS.MAINNET) &&
                    isTokenAddressNativeAsset(sellToken?.address, sellToken?.chainId) ? (
                    <p>
                      Matcha Auto currently doesn&apos;t support selling the{' '}
                      {sellToken?.symbol ?? 'selected sell'} token.
                    </p>
                  ) : (
                    <p>
                      Matcha Auto is not available on the selected network. To use Matcha Auto,
                      switch to {supportedChainsPhrase}.
                    </p>
                  )}
                </MatchaTooltipContent>
                <MatchaTooltipPopover sideOffset={10} />
              </MatchaTooltip>
            )}

            <ToggleGroup.Item
              className={fontStyles({
                variant: 'LABEL_MD',
                class:
                  'text-token-gray-1600 flex w-full h-7 px-4 border-none items-center rounded-lg justify-center hover opacity-100 aria-checked:bg-special-background-button-medium-secondary aria-checked:shadow-05 aria-checked:transition-all aria-checked:duration-200 aria-checked:opacity-100 disabled:text-token-gray-900 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-token-gray-900 disabled:hover:cursor-not-allowed disabled:hover:opacity-50 disabled:aria-checked:text-token-gray-900 disabled-aria-checked:cursor-not-allowed disabled:aria-checked:opacity-50 focus-visible:outline-2 focus-visible:outline-token-gray-400 focus:active:outline-0',
              })}
              value="off"
              disabled={!isGaslessToggleEnabled}
            >
              Off
            </ToggleGroup.Item>
          </ToggleGroup.Root>
        </div>

        <div className={fontStyles({ variant: 'BODY_SM', class: 'text-token-gray-900' })}>
          Your trading autopilot companion. When available it takes care of all the complex
          execution settings of your swap, enables gas-free trading, and ensure you will get the
          most out of your trade.{' '}
          <ExternalLink
            href={routes.MATCHA_AUTO_BLOG_POST}
            className={linkStyles({ color: 'purple', underline: true })}
            onClick={() => ampli.learnMoreClicked({ ui_component: 'matcha_auto_settings' })}
          >
            Learn more
          </ExternalLink>
        </div>
      </SectionBody>
    </Section>
  );
};
